import { useState, useRef, useEffect } from 'react';

const App = () => {
  const [apiKey, setApiKey] = useState('');
  const [bookText, setBookText] = useState('');
  const [selection, setSelection] = useState({ text: '', start: 0, end: 0 });
  const [prompt, setPrompt] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [apiKeySet, setApiKeySet] = useState(false);
  const [bookTitle, setBookTitle] = useState('My Book');
  const [notification, setNotification] = useState('');
  const textAreaRef = useRef(null);
  const fileInputRef = useRef(null);

  // Load saved data on initial render
  useEffect(() => {
    // Load API key from localStorage
    const savedApiKey = localStorage.getItem('openai_api_key');
    if (savedApiKey) {
      setApiKey(savedApiKey);
      setApiKeySet(true);
    }

    // Load book text from localStorage
    const savedText = localStorage.getItem('bookText');
    if (savedText) {
      setBookText(savedText);
    }

    // Load book title from localStorage
    const savedTitle = localStorage.getItem('bookTitle');
    if (savedTitle) {
      setBookTitle(savedTitle);
    }
  }, []);

  // Handle text selection in the textarea
  const handleSelect = () => {
    const textarea = textAreaRef.current;
    if (textarea.selectionStart !== textarea.selectionEnd) {
      setSelection({
        text: bookText.substring(textarea.selectionStart, textarea.selectionEnd),
        start: textarea.selectionStart,
        end: textarea.selectionEnd
      });
    }
  };

  // Reset selection when text changes
  useEffect(() => {
    setSelection({ text: '', start: 0, end: 0 });
  }, [bookText]);

  // Save API key to localStorage
  const saveApiKey = () => {
    if (apiKey.trim()) {
      localStorage.setItem('openai_api_key', apiKey);
      setApiKeySet(true);
      setError('');
      showNotification('API key saved successfully');
    } else {
      setError('Please enter a valid API key');
    }
  };

  // Save book title to localStorage
  useEffect(() => {
    if (bookTitle) {
      localStorage.setItem('bookTitle', bookTitle);
    }
  }, [bookTitle]);

  // Auto-save book text to localStorage
  useEffect(() => {
    if (bookText) {
      localStorage.setItem('bookText', bookText);
    }
  }, [bookText]);

  // Get context before and after selection
  const getContext = () => {
    // Get about 200 characters before and after the selection for context
    const contextBefore = bookText.substring(
      Math.max(0, selection.start - 200), 
      selection.start
    );
    
    const contextAfter = bookText.substring(
      selection.end, 
      Math.min(bookText.length, selection.end + 200)
    );
    
    return { contextBefore, contextAfter };
  };

  // Apply AI suggestion to replace selected text
  const applyAiSuggestion = async () => {
    if (!selection.text) {
      setError('Please select text to enhance');
      return;
    }
    
    if (!prompt) {
      setError('Please enter a prompt for the AI');
      return;
    }
    
    setIsLoading(true);
    setError('');
    
    try {
      const { contextBefore, contextAfter } = getContext();
      
      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        },
        body: JSON.stringify({
          model: 'gpt-4',
          messages: [
            {
              role: 'system',
              content: 'You are an assistant helping a writer with their book. You will be given text to modify based on specific instructions.'
            },
            {
              role: 'user',
              content: `I'm writing a book and need help modifying a section.
              
Here's the context before the section:
"""
${contextBefore}
"""

Here's the section I want to modify:
"""
${selection.text}
"""

Here's the context after the section:
"""
${contextAfter}
"""

I want you to: ${prompt}

Please provide ONLY the replacement text for the selected section. Don't include any explanations or additional notes.`
            }
          ],
          temperature: 0.7
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Failed to get AI response');
      }
      
      const data = await response.json();
      const replacementText = data.choices[0].message.content.trim();
      
      // Replace the selected text with the AI response
      const newText = 
        bookText.substring(0, selection.start) + 
        replacementText + 
        bookText.substring(selection.end);
      
      setBookText(newText);
      showNotification('AI enhancement applied successfully');
      
    } catch (err) {
      setError(err.message || 'Failed to get AI response');
    } finally {
      setIsLoading(false);
    }
  };

  // Display temporary notification
  const showNotification = (message) => {
    setNotification(message);
    setTimeout(() => {
      setNotification('');
    }, 3000);
  };

  // Export book to file
  const exportBook = () => {
    const bookData = {
      title: bookTitle,
      content: bookText,
      lastModified: new Date().toISOString()
    };
    
    const blob = new Blob([JSON.stringify(bookData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    const a = document.createElement('a');
    a.href = url;
    a.download = `${bookTitle.replace(/\s+/g, '_')}.book.json`;
    document.body.appendChild(a);
    a.click();
    
    // Cleanup
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    showNotification('Book exported successfully');
  };

  // Import book from file
  const importBook = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const bookData = JSON.parse(event.target.result);
        if (bookData.title && bookData.content) {
          setBookTitle(bookData.title);
          setBookText(bookData.content);
          showNotification('Book imported successfully');
        } else {
          setError('Invalid book file format');
        }
      } catch (err) {
        setError('Failed to parse book file');
      }
    };
    
    reader.readAsText(file);
    // Reset file input
    e.target.value = '';
  };

  // Trigger file input click
  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  // Clear all data
  const clearAllData = () => {
    if (window.confirm('Are you sure you want to clear all data? This cannot be undone.')) {
      localStorage.removeItem('openai_api_key');
      localStorage.removeItem('bookText');
      localStorage.removeItem('bookTitle');
      setApiKey('');
      setApiKeySet(false);
      setBookText('');
      setBookTitle('My Book');
      showNotification('All data cleared successfully');
    }
  };

  // Styles - basic CSS without Tailwind or shadcn
  const styles = {
    container: {
      fontFamily: 'Arial, sans-serif',
      maxWidth: '1000px',
      margin: '0 auto',
      padding: '20px',
    },
    header: {
      backgroundColor: '#4a6da7',
      color: 'white',
      padding: '15px 20px',
      borderRadius: '5px',
      marginBottom: '20px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    title: {
      fontSize: '24px',
      margin: '0',
    },
    card: {
      backgroundColor: 'white',
      padding: '20px',
      borderRadius: '5px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      marginBottom: '20px',
    },
    input: {
      width: '100%',
      padding: '10px',
      border: '1px solid #ccc',
      borderRadius: '4px',
      fontSize: '16px',
      marginBottom: '10px',
    },
    textarea: {
      width: '100%',
      height: '400px',
      padding: '10px',
      border: '1px solid #ccc',
      borderRadius: '4px',
      fontSize: '16px',
      fontFamily: 'Georgia, serif',
      marginBottom: '10px',
      resize: 'vertical',
    },
    button: {
      backgroundColor: '#4a6da7',
      color: 'white',
      padding: '10px 15px',
      border: 'none',
      borderRadius: '4px',
      fontSize: '16px',
      cursor: 'pointer',
      marginRight: '10px',
    },
    secondaryButton: {
      backgroundColor: '#6c757d',
      color: 'white',
      padding: '10px 15px',
      border: 'none',
      borderRadius: '4px',
      fontSize: '16px',
      cursor: 'pointer',
      marginRight: '10px',
    },
    dangerButton: {
      backgroundColor: '#dc3545',
      color: 'white',
      padding: '10px 15px',
      border: 'none',
      borderRadius: '4px',
      fontSize: '16px',
      cursor: 'pointer',
    },
    disabledButton: {
      backgroundColor: '#cccccc',
      color: '#666666',
      padding: '10px 15px',
      border: 'none',
      borderRadius: '4px',
      fontSize: '16px',
      cursor: 'not-allowed',
    },
    flexRow: {
      display: 'flex',
      gap: '10px',
      alignItems: 'center',
    },
    buttonGroup: {
      display: 'flex',
      gap: '10px',
      marginTop: '15px',
    },
    error: {
      color: 'red',
      marginTop: '5px',
    },
    notification: {
      backgroundColor: '#d4edda',
      color: '#155724',
      padding: '10px 15px',
      borderRadius: '4px',
      marginBottom: '15px',
      display: notification ? 'block' : 'none',
    },
    selectedText: {
      padding: '10px',
      backgroundColor: '#f3f3f3',
      border: 'solid 1px #ddd',
      borderLeft: 'solid 4px #4a6da7',
      borderRadius: '4px',
      marginBottom: '10px',
    },
    label: {
      display: 'block',
      marginBottom: '5px',
      fontWeight: 'bold',
    },
    footer: {
      textAlign: 'center',
      color: '#666',
      fontSize: '14px',
      margin: '20px 0',
    },
    smallText: {
      fontSize: '14px',
      color: '#666',
    },
    hiddenInput: {
      display: 'none',
    }
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <h1 style={styles.title}>AI-Assisted Book Writer</h1>
        {apiKeySet && (
          <div style={styles.flexRow}>
            <button onClick={exportBook} style={styles.secondaryButton}>Export Book</button>
            <button onClick={triggerFileInput} style={styles.secondaryButton}>Import Book</button>
            <input 
              type="file" 
              ref={fileInputRef} 
              onChange={importBook} 
              accept=".json,.book.json" 
              style={styles.hiddenInput} 
            />
          </div>
        )}
      </header>

      <main>
        {/* Notification */}
        {notification && (
          <div style={styles.notification}>{notification}</div>
        )}

        {/* API Key Section */}
        {!apiKeySet ? (
          <div style={styles.card}>
            <h2>Enter your OpenAI API Key</h2>
            <div style={styles.flexRow}>
              <input
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="sk-..."
                style={{...styles.input, flexGrow: 1}}
              />
              <button
                onClick={saveApiKey}
                style={styles.button}
              >
                Save Key
              </button>
            </div>
            {error && <p style={styles.error}>{error}</p>}
            <p style={styles.smallText}>
              Your API key is stored only in your browser's local storage and never sent to any server except OpenAI.
            </p>
          </div>
        ) : (
          <>
            {/* Book Title */}
            <div style={styles.card}>
              <h2>Book Title</h2>
              <input
                type="text"
                value={bookTitle}
                onChange={(e) => setBookTitle(e.target.value)}
                placeholder="Enter book title..."
                style={styles.input}
              />
            </div>

            {/* Main Editor */}
            <div style={styles.card}>
              <h2>Write Your Book</h2>
              <p style={styles.smallText}>
                Select text and use the panel below to enhance it with AI. Your work is automatically saved in your browser.
              </p>
              <textarea
                ref={textAreaRef}
                value={bookText}
                onChange={(e) => setBookText(e.target.value)}
                onSelect={handleSelect}
                placeholder="Start writing your masterpiece..."
                style={styles.textarea}
              ></textarea>
            </div>

            {/* AI Enhancement Panel */}
            <div style={styles.card}>
              <h2>AI Enhancement</h2>
              {selection.text ? (
                <div>
                  <div>
                    <label style={styles.label}>
                      Selected Text:
                    </label>
                    <div style={styles.selectedText}>
                      {selection.text}
                    </div>
                  </div>
                  <div>
                    <label style={styles.label}>
                      Prompt for AI (e.g., "add more suspense here"):
                    </label>
                    <input
                      type="text"
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                      placeholder="What would you like the AI to do with this text?"
                      style={styles.input}
                    />
                  </div>
                  <button
                    onClick={applyAiSuggestion}
                    disabled={isLoading}
                    style={isLoading ? styles.disabledButton : styles.button}
                  >
                    {isLoading ? 'Processing...' : 'Apply AI Suggestion'}
                  </button>
                  {error && <p style={styles.error}>{error}</p>}
                </div>
              ) : (
                <p style={{fontStyle: 'italic', color: '#666'}}>
                  Select some text in your book to enhance it with AI.
                </p>
              )}
            </div>

            {/* Settings */}
            <div style={styles.card}>
              <h2>Settings</h2>
              <div style={styles.buttonGroup}>
                <button onClick={() => setApiKeySet(false)} style={styles.secondaryButton}>
                  Change API Key
                </button>
                <button onClick={clearAllData} style={styles.dangerButton}>
                  Clear All Data
                </button>
              </div>
            </div>
          </>
        )}
      </main>

      <footer style={styles.footer}>
        <p>AI Book Writer - Your work is saved locally in your browser</p>
      </footer>
    </div>
  );
};

export default App;